import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { baseURL } from '../../../api/url';

/**
 * Dedicated client for the registration flow. It deliberately has no
 * interceptors: a 401 here must surface to the caller instead of redirecting
 * to /login, because the visitor has no account yet.
 */
export const registrationClient = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' }
});

export class RegistrationApiError extends Error {
  code: string;
  status: number;
  meta?: unknown;
  /** Seconds to wait before asking again; set on every 429. */
  retryAfterSeconds?: number;

  constructor(code: string, message: string, status: number, meta?: unknown) {
    super(message);
    this.name = 'RegistrationApiError';
    this.code = code;
    this.status = status;
    this.meta = meta;
    Object.setPrototypeOf(this, RegistrationApiError.prototype);
  }
}

export interface CheckoutSessionInfo {
  bizToken: string | null;
  faceIndex: string | null;
  paymentRequired: boolean;
  storeName: string;
  amount: number;
  currency: string;
  prefill?: { phone_number: string; country_code: string };
  phoneVerificationToken?: string;
}

/** A created account's token, or the answer to a test registration. */
export type CreateAccountResult =
  { accessToken: string; exp: number } | { dryRun: true };

export interface CreateAccountBody {
  phoneVerificationToken: string;
  emailVerificationToken: string;
  name: string;
  email: string;
  phoneNumber: string;
  countryCode: string;
  nationality: string;
  securityPhrase: string;
  securityAvatarToken: string;
  termsAccepted: true;
  face: { faceIndex: string };
  /** "true" asks for a test registration: every check runs and nothing is created. */
  dry_run?: 'true';
  origin: {
    kind: 'direct' | 'checkout' | 'onglaye' | 'external';
    sessionId?: string;
    key?: string;
    referralCode?: string;
  };
}

/** One pickable avatar: an opaque token to send back and a data-URI image. */
export interface AvatarCandidate {
  token: string;
  image: string;
}

export interface StartLinkBody {
  destination: 'home' | 'checkoutRegistration' | 'onglayeRegistration';
  payment_method: string;
  checkout_session_id?: string;
  ong_key?: string;
}

interface Envelope<T> {
  success: boolean;
  status?: number;
  data: T;
  error?: { code?: string; message?: string; meta?: unknown };
}

/** Used when a 429 arrives without a usable Retry-After header. */
export const DEFAULT_RETRY_AFTER_SECONDS = 10;

/** Reads Retry-After as delta-seconds or an HTTP date. */
const parseRetryAfter = (raw: unknown): number => {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const seconds = Number(value.trim());
    if (Number.isFinite(seconds) && seconds >= 0) return seconds;
    const date = Date.parse(value);
    if (!Number.isNaN(date)) {
      return Math.max(0, Math.ceil((date - Date.now()) / 1000));
    }
  }
  return DEFAULT_RETRY_AFTER_SECONDS;
};

const readHeader = (headers: unknown, name: string): unknown => {
  if (!headers || typeof headers !== 'object') return undefined;
  const h = headers as { get?: (n: string) => unknown } & Record<
    string,
    unknown
  >;
  if (typeof h.get === 'function') {
    const v = h.get(name);
    if (v !== undefined && v !== null) return v;
  }
  const key = Object.keys(h).find((k) => k.toLowerCase() === name);
  return key ? h[key] : undefined;
};

/** Any failed request as a `RegistrationApiError`; a 429 carries `retryAfterSeconds`. */
export const toApiError = (error: unknown): RegistrationApiError => {
  if (error instanceof RegistrationApiError) return error;
  if (axios.isAxiosError(error)) {
    const e = error as AxiosError<Envelope<unknown>>;
    const body = e.response?.data;
    const apiError = new RegistrationApiError(
      body?.error?.code ?? (e.response ? 'REQUEST_FAILED' : 'NETWORK_ERROR'),
      body?.error?.message ?? e.message,
      e.response?.status ?? 0,
      body?.error?.meta
    );
    if (e.response?.status === 429) {
      apiError.retryAfterSeconds = parseRetryAfter(
        readHeader(e.response.headers, 'retry-after')
      );
    }
    return apiError;
  }
  return new RegistrationApiError(
    'NETWORK_ERROR',
    error instanceof Error ? error.message : 'Request failed',
    0
  );
};

const request = async <T>(config: AxiosRequestConfig): Promise<T> => {
  try {
    const res = await registrationClient.request<Envelope<T>>(config);
    const body = res.data;
    if (body && body.success === false) {
      throw new RegistrationApiError(
        body.error?.code ?? 'REQUEST_FAILED',
        body.error?.message ?? 'Request failed',
        res.status,
        body.error?.meta
      );
    }
    return body.data;
  } catch (error) {
    throw toApiError(error);
  }
};

export const registrationApi = {
  validateCheckout: (id: string) =>
    request<CheckoutSessionInfo>({
      method: 'GET',
      url: '/checkout/validate',
      params: { checkout_session_id: id }
    }),

  geo: () =>
    request<{ countryCode: string; dialCode: string }>({
      method: 'GET',
      url: '/auth/geo'
    }),

  startFaceScan: async () => {
    const d = await request<{ VerificationUrl: string; BizToken: string }>({
      method: 'POST',
      url: '/face/register',
      data: { screen: 'register' }
    });
    return { verificationUrl: d.VerificationUrl, bizToken: d.BizToken };
  },

  sendPhoneOtp: async (b: {
    phoneNumber: string;
    countryCode: string;
    channel: 'sms' | 'whatsapp';
  }): Promise<void> => {
    await request<unknown>({
      method: 'POST',
      url:
        b.channel === 'whatsapp'
          ? '/auth/register/phone/send-otp-whatsapp'
          : '/auth/register/phone/send-otp',
      data: { phoneNumber: b.phoneNumber, countryCode: b.countryCode }
    });
  },

  sendEmailOtp: async (b: { email: string }): Promise<void> => {
    await request<unknown>({
      method: 'POST',
      url: '/auth/register/email/send-otp',
      data: { email: b.email }
    });
  },

  verifyPhoneOtp: async (b: {
    phoneNumber: string;
    countryCode: string;
    otp: string;
  }): Promise<string> => {
    const d = await request<{ verificationToken: string }>({
      method: 'POST',
      url: '/auth/register/phone/verify-otp',
      data: b
    });
    return d.verificationToken;
  },

  verifyEmailOtp: async (b: {
    email: string;
    otp: string;
  }): Promise<string> => {
    const d = await request<{ verificationToken: string }>({
      method: 'POST',
      url: '/auth/register/email/verify-otp',
      data: b
    });
    return d.verificationToken;
  },

  createAccount: (b: CreateAccountBody) =>
    request<CreateAccountResult>({
      method: 'POST',
      url: '/registration',
      data: b,
      withCredentials: true
    }),

  getAvatarCandidates: () =>
    request<{ candidates: AvatarCandidate[] }>({
      method: 'GET',
      url: '/registration/security-avatars/candidates'
    }),

  resolveGateway: (paymentMethod: string) =>
    request<{ gateway: 'fiuu' | 'stripe' }>({
      method: 'GET',
      url: '/payments-consent/resolve-gateway',
      params: { payment_method: paymentMethod }
    }),

  startLink: (b: StartLinkBody, accessToken: string) =>
    request<{ payUrl?: string }>({
      method: 'POST',
      url: '/payments-consent/pay',
      data: b,
      headers: { Authorization: `Bearer ${accessToken}` }
    })
};
